/**
 * RequireRole — gates a route on a MINIMUM role, respecting the hierarchy
 * `reviewer ⊂ operator ⊂ admin` (compared by rank via roleAtLeast, never `===`).
 *
 * Assumes it is rendered inside <RequireAuth> (a user exists). If the user's
 * role is insufficient, renders a forbidden state rather than redirecting to
 * sign-in — they ARE signed in, just not authorized.
 */

import type { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import type { Role } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { roleAtLeast, roleLabel } from "../auth/roles";
import { EmptyState } from "./EmptyState";

export function RequireRole({
  role,
  children,
}: {
  role: Role;
  children: ReactNode;
}) {
  const { role: current } = useAuth();

  if (!roleAtLeast(current, role)) {
    return (
      <Box sx={{ py: 4 }}>
        <EmptyState
          icon={<LockOutlinedIcon fontSize="inherit" />}
          title="Acceso restringido"
          description={`Esta sección requiere el rol de ${roleLabel(role)} o superior. Tu rol actual no tiene permiso.`}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center" }}
        >
          AUTH_FORBIDDEN_ROLE
        </Typography>
      </Box>
    );
  }
  return <>{children}</>;
}
